// Data-klassifisering + klarerings-sjekker (sikkerhetsloven / NATO). Kjernen i et
// gradert C2-system: hver informasjonsbit har et graderingsnivå, hver bruker en
// klarering, og «no read up» (Bell-LaPadula) håndheves — en bruker ser aldri noe
// høyere enn sin klarering. Ren, testbar logikk; håndheves autoritativt i backend
// (jf. grunnlov §4 default deny) i tillegg til RBAC org-scope.
//
// NB: `intel.ts` har en egen, smalere `Classification` (rapportmerking, 4 nivåer).
// Denne modellen (`SecurityLevel`) er den fulle sikkerhetsloven-skalaen (5 nivåer,
// inkl. STRENGT HEMMELIG) og brukes til klarering/tilgangskontroll på tvers.
import { z } from 'zod';
/** Norske graderingsnivåer (sikkerhetsloven), laveste → høyeste. */
export const SecurityLevel = z.enum([
    'UGRADERT',
    'BEGRENSET',
    'KONFIDENSIELT',
    'HEMMELIG',
    'STRENGT HEMMELIG',
]);
export const CLASS_ORDER = [
    'UGRADERT', 'BEGRENSET', 'KONFIDENSIELT', 'HEMMELIG', 'STRENGT HEMMELIG',
];
/** NATO-ekvivalenter (til interop/banner). */
export const CLASS_NATO = {
    UGRADERT: 'NATO UNCLASSIFIED',
    BEGRENSET: 'NATO RESTRICTED',
    KONFIDENSIELT: 'NATO CONFIDENTIAL',
    HEMMELIG: 'NATO SECRET',
    'STRENGT HEMMELIG': 'COSMIC TOP SECRET',
};
/** Bannerfarge (COP-markering). */
export const CLASS_COLOR = {
    UGRADERT: '#4bd15b',
    BEGRENSET: '#5b9bd5',
    KONFIDENSIELT: '#e0a24a',
    HEMMELIG: '#ff8a3d',
    'STRENGT HEMMELIG': '#ff3b3b',
};
/** Rang (0 = UGRADERT … 4 = STRENGT HEMMELIG). -1 for ukjent. */
export function classRank(c) {
    return CLASS_ORDER.indexOf(c);
}
/** Coerce vilkårlig verdi til gyldig nivå (fallback = UGRADERT, fail-safe lavt). */
export function toSecurityLevel(v) {
    const r = SecurityLevel.safeParse(typeof v === 'string' ? v.toUpperCase() : v);
    return r.success ? r.data : 'UGRADERT';
}
/** «No read up»: klarering må dominere (≥) graderingen for lesetilgang. */
export function canRead(clearance, dataClass) {
    return classRank(clearance) >= classRank(dataClass);
}
/** Høyeste gradering i en samling (til roll-up-banner for et sammensatt bilde). */
export function highestClass(items) {
    return items.reduce((hi, c) => (classRank(c) > classRank(hi) ? c : hi), 'UGRADERT');
}
/** Bannerlinje med valgfri frigivnings-caveat («REL TO …»). */
export function markingLine(c, releasableTo) {
    const rel = releasableTo && releasableTo.length ? ` // REL TO ${releasableTo.join(', ')}` : '';
    return `${c}${rel}`;
}
/** Filtrer en liste graderte elementer til det en klarering får lese. */
export function filterByClearance(items, clearance, classOf) {
    return items.filter((t) => canRead(clearance, classOf(t)));
}
//# sourceMappingURL=classification.js.map